// src/components/UserContextViewer.js
import React, { useEffect, useState } from 'react';
import supabase from '../supabase';

const UserContextViewer = ({ user }) => {
  const [groupedSettings, setGroupedSettings] = useState({}); // Grouped by "grouping"
  const [editedSettings, setEditedSettings] = useState({});   // Flat object: { key: value }
  const [loading, setLoading] = useState(true);
  const [savingField, setSavingField] = useState(null);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(''); // Active group/tab

  // Helper to convert snake_case into Title Case (e.g., first_name => First Name)
  const formatKey = (key) => {
    return key
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  useEffect(() => {
    if (user) {
      fetchSettings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Fetch both the definitions (question_mappings) and the user's current values (user_context)
  const fetchSettings = async () => {
    if (!user?.id) return;
    setLoading(true);
    setError(null);

    // 1. Fetch question mappings (only settings that aren’t hidden, ordered as desired)
    const { data: mappings, error: mappingError } = await supabase
      .from('question_mappings')
      .select('*')
      .eq('hidden', false)
      .order('order_index', { ascending: true });

    if (mappingError) {
      setError('Failed to load settings definitions.');
      console.error(mappingError);
      setLoading(false);
      return;
    }

    // 2. Fetch the user's current context/settings values
    const { data: userContextData, error: userContextError } = await supabase
      .from('user_context')
      .select('*')
      .eq('user_id', user.id);

    if (userContextError) {
      setError('Failed to load user context.');
      console.error(userContextError);
      setLoading(false);
      return;
    }

    // Convert the user_context data into a lookup object by key.
    const userValues = {};
    userContextData.forEach(item => {
      userValues[item.key] = item.value;
    });

    // 3. Merge the mappings with the user's current values.
    // We assume each mapping has a "mapping_key", and optionally a "question".
    const combined = mappings.map(mapping => ({
      key: mapping.mapping_key,
      question: mapping.question || formatKey(mapping.mapping_key),
      description: mapping.description || '',
      grouping: mapping.grouping || 'Uncategorized',
      order_index: mapping.order_index,
      setting_type: mapping.setting_type || 'text', // default to text
      options: mapping.options || null, // could be an array for select/boolean options
      value: userValues[mapping.mapping_key] || '',
    }));

    // 4. Group the combined settings by "grouping"
    const grouped = combined.reduce((acc, item) => {
      const group = item.grouping;
      if (!acc[group]) acc[group] = [];
      acc[group].push(item);
      return acc;
    }, {});

    setGroupedSettings(grouped);

    // 5. Create a flat object for editing purposes.
    const flat = {};
    combined.forEach(item => {
      flat[item.key] = item.value;
    });
    setEditedSettings(flat);

    // Set the active tab to the first group if not already set.
    const groups = Object.keys(grouped);
    if (groups.length > 0 && !activeTab) {
      setActiveTab(groups[0]);
    }

    setLoading(false);
  };

  const handleFieldChange = (key, newValue) => {
    setEditedSettings(prev => ({
      ...prev,
      [key]: newValue,
    }));
  };

  // Save or update the field using an upsert.
  const saveField = async (key) => {
    if (!user?.id) return;
    setSavingField(key);
    const newValue = editedSettings[key];

    const { error } = await supabase
      .from('user_context')
      .upsert({ user_id: user.id, key, value: newValue }, { onConflict: ['user_id', 'key'] });

    if (error) {
      setError(`Failed to update ${key}.`);
      console.error(error);
    } else {
      // Update local grouped data with the saved value.
      const updatedGrouped = { ...groupedSettings };
      Object.keys(updatedGrouped).forEach(group => {
        updatedGrouped[group] = updatedGrouped[group].map(item =>
          item.key === key ? { ...item, value: newValue } : item
        );
      });
      setGroupedSettings(updatedGrouped);
    }
    setSavingField(null);
  };

  // Helper to render the proper input based on the setting type.
  const renderInputField = (item) => {
    const commonProps = {
      value: editedSettings[item.key] || '',
      onChange: (e) => handleFieldChange(item.key, e.target.value),
      className: "w-full bg-gray-700 text-white border border-gray-600 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
    };

    switch (item.setting_type) {
      case 'textarea':
        return (
          <textarea
            {...commonProps}
            rows="4"
          />
        );
      case 'boolean':
        return (
          <select {...commonProps}>
            { item.options ? (
              item.options.map(option => (
                <option key={option} value={option}>{option}</option>
              ))
            ) : (
              <>
                <option value="true">Yes</option>
                <option value="false">No</option>
              </>
            )}
          </select>
        );
      case 'select':
        return (
          <select {...commonProps}>
            <option value="">Select an option</option>
            { item.options && item.options.map(option => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        );
      default:
        return (
          <input
            type="text"
            {...commonProps}
          />
        );
    }
  };

  // Get available groups from grouped settings.
  const groups = Object.keys(groupedSettings);

  // Show only the fields for the active group.
  const fieldsToShow = activeTab ? groupedSettings[activeTab] || [] : [];

  return (
    <div className="p-6 bg-gray-900 text-white rounded-md shadow-md max-w-5xl mx-auto">
      <h2 className="text-2xl font-semibold mb-6">Settings</h2>

      {/* Tabbed Navigation (by group) */}
      <div className="mb-6 border-b border-gray-700">
        <nav className="flex space-x-6">
          {groups.map(group => (
            <button
              key={group}
              className={`pb-2 transition border-b-2 ${
                activeTab === group
                  ? 'border-blue-500 text-white'
                  : 'border-transparent text-gray-400 hover:text-white'
              }`}
              onClick={() => setActiveTab(group)}
            >
              {group}
            </button>
          ))}
        </nav>
      </div>

      {loading ? (
        <p className="text-gray-400">Loading...</p>
      ) : error ? (
        <p className="text-red-500">{error}</p>
      ) : fieldsToShow.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {fieldsToShow.map(item => (
            <div 
              key={item.key} 
              className={`bg-gray-800 p-4 rounded shadow ${
                item.setting_type === 'textarea' ? 'md:col-span-2' : ''
              }`}
            >
              <label className="block text-green-400 font-semibold mb-1">
                {item.question}
              </label>
              {item.description && (
                <p className="text-gray-400 text-sm mb-2">{item.description}</p>
              )}
              {item.setting_type === 'textarea' ? (
                <>
                  {renderInputField(item)}
                  <button
                    onClick={() => saveField(item.key)}
                    disabled={savingField === item.key}
                    className="mt-2 block w-full px-3 py-2 bg-green-600 rounded hover:bg-green-700 transition"
                  >
                    {savingField === item.key ? 'Saving...' : 'Save'}
                  </button>
                </>
              ) : (
                <div className="flex items-center space-x-2">
                  {renderInputField(item)}
                  <button
                    onClick={() => saveField(item.key)}
                    disabled={savingField === item.key}
                    className="px-3 py-2 bg-green-600 rounded hover:bg-green-700 transition whitespace-nowrap"
                  >
                    {savingField === item.key ? 'Saving...' : 'Save'}
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <p className="text-gray-400">No settings found.</p>
      )}

      <button
        onClick={fetchSettings}
        className="mt-8 px-4 py-2 bg-blue-600 rounded hover:bg-blue-700 transition"
      >
        Refresh Settings
      </button>
    </div>
  );
};

export default UserContextViewer;
